import { useContext, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { UNSAFE_NavigationContext as NavigationContext, useLocation } from "react-router-dom";
import { useNavBarTabId } from "../shell/NavBarScope";
import { hasUnsavedChanges, requestLeave, useLeaveGuardStore } from "../shell/leaveGuardStore";
import { overrideNavigator } from "./overrideNavigator";

/** BrowserRouter has no blocker API. Intercept indexed POP before its listener receives it,
 * restore the accepted entry, and only replay the requested step after explicit discard.
 * The blocked route never renders, so the editor and its local draft stay mounted. */
export function BrowserHistoryLeaveGuard() {
    const location = useLocation();
    const scope = useNavBarTabId();
    const accepted = useRef<number>();
    const acceptedKey = useRef<unknown>();
    const restoring = useRef<{ from: number; fromKey: unknown; to: number; toKey: unknown }>();
    const replay = useRef<{ index: number; key: unknown }>();
    useLayoutEffect(() => {
        const index: unknown = window.history.state?.idx;
        if (typeof index === "number") {
            accepted.current = index;
            acceptedKey.current = window.history.state?.key;
        }
    }, [location]);
    useLayoutEffect(() => {
        let active = true;
        const pop = (event: PopStateEvent) => {
            const index: unknown = event.state?.idx;
            if (typeof index !== "number" || accepted.current === undefined) return;
            const key: unknown = event.state?.key;
            const blocked = restoring.current;
            if (blocked) {
                if (index === blocked.from && key !== blocked.fromKey) {
                    // A replacement at the same index supersedes the interrupted traversal.
                    restoring.current = undefined;
                    accepted.current = index;
                    acceptedKey.current = key;
                    return;
                }
                event.stopImmediatePropagation();
                if (index !== blocked.from) {
                    window.history.go(blocked.from - index);
                    return;
                }
                restoring.current = undefined;
                requestLeave(
                    () => {
                        // A later navigation supersedes this prompt's history operation.
                        if (
                            !active ||
                            window.history.state?.idx !== blocked.from ||
                            window.history.state?.key !== blocked.fromKey
                        )
                            return;
                        replay.current = { index: blocked.to, key: blocked.toKey };
                        window.history.go(blocked.to - blocked.from);
                    },
                    { scope },
                );
                return;
            }
            if (index === replay.current?.index && key === replay.current.key) {
                replay.current = undefined;
                accepted.current = index;
                acceptedKey.current = key;
                return;
            }
            replay.current = undefined;
            if (
                index === accepted.current ||
                !hasUnsavedChanges(scope) ||
                !useLeaveGuardStore.getState().hosts.length
            ) {
                accepted.current = index;
                acceptedKey.current = key;
                return;
            }
            event.stopImmediatePropagation();
            restoring.current = {
                from: accepted.current,
                fromKey: acceptedKey.current,
                to: index,
                toKey: key,
            };
            window.history.go(accepted.current - index);
        };
        window.addEventListener("popstate", pop, true);
        return () => {
            active = false;
            restoring.current = undefined;
            replay.current = undefined;
            window.removeEventListener("popstate", pop, true);
        };
    }, [scope]);
    return null;
}

/** Native hosts use MemoryRouter; guard numeric Back/Forward without changing its route model. */
export function MemoryHistoryLeaveGuard({ children }: { children: ReactNode }) {
    const context = useContext(NavigationContext);
    const scope = useNavBarTabId();
    const value = useMemo(
        () => ({
            ...context,
            navigator: overrideNavigator(context.navigator, {
                go: (delta: number) => requestLeave(() => context.navigator.go(delta), { scope }),
            }),
        }),
        [context, scope],
    );
    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
